import { apiClientMethods } from '../lib/api-client';
import type { User } from '../types/api';

export interface UpdateProfileRequest {
  businessName?: string;
  email?: string;
  phoneNumber?: string;
  passFeeToCustomer?: boolean;
}

export interface UpdateProfileResponse {
  id: string;
  businessName: string;
  email: string;
  phoneNumber: string;
  emailVerified: boolean;
  phoneVerified?: boolean;
  kycStatus: string;
  passFeeToCustomer?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPreferences {
  emailNotifications?: boolean;
  webhookNotifications?: boolean;
  notifyOnSuccess?: boolean;
  notifyOnFailure?: boolean;
}

export interface UpdateNotificationPreferencesResponse {
  id?: string;
  merchantId?: string;
  emailNotifications: boolean;
  webhookNotifications: boolean;
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
}

export interface MerchantNotification {
  id: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'TRANSACTION' | 'KYC' | 'SYSTEM';
  isRead: boolean;
  createdAt: string;
}

export interface NotificationListResponse {
  data: MerchantNotification[];
  pagination?: { page?: number; limit?: number; total?: number; totalUnread?: number };
}

export interface CredentialsResponse {
  apiKey: string;
  apiSecret: string;
  apiSecretFull: string;
  webhookUrl?: string | null;
  webhookPaused?: boolean;
  webhookSecret?: string;
  webhookSecretFull?: string;
  ipWhitelist: string[];
}

export interface WebhookDeliveryIssue {
  id: string;
  status: 'PENDING' | 'FAILED';
  attempts: number;
  responseCode: number | null;
  responseBody: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateWebhookUrlResponse {
  message: string;
  webhookUrl: string | null;
}

export interface RegenerateWebhookSecretResponse {
  message: string;
  webhookSecret: string;
}

export interface TestWebhookResponse {
  success: boolean;
  statusCode?: number;
  message: string;
  responseBody?: string;
  durationMs?: number;
}

export interface RegenerateCredentialsResponse {
  apiKey: string;
  apiSecret: string;
  message: string;
}

export interface BalanceDetailsResponse {
  availableBalance: number;
  totalBalance?: number;
  securityAmount?: number;
  heldAmount?: number;
  settlementHoldDetails?: {
    hours: number;
    activeHoldsCount: number;
    nextReleaseAt?: string;
  };
  totalVolume: number;
  platformFees: number;
  totalTransactions: number;
  currency: string;
  lastUpdated: string;
  currentFeePercentage?: number;
}

export interface ReactivateAccountResponse {
  message: string;
  dormantAt: string | null;
  processingDisabledAt: string | null;
  kycRefreshRequiredAt: string | null;
  reactivatedAt: string | null;
}

export class MerchantService {
  /**
   * Update merchant profile information
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    try {
      return apiClientMethods.put<UpdateProfileResponse>('/merchants/profile', data);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  async updateNotificationPreferences(
    preferences: NotificationPreferences
  ): Promise<UpdateNotificationPreferencesResponse> {
    try {
      return apiClientMethods.patch<UpdateNotificationPreferencesResponse>(
        '/notifications/settings',
        preferences
      );
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error;
    }
  }

  async getNotificationPreferences(): Promise<UpdateNotificationPreferencesResponse> {
    return apiClientMethods.get<UpdateNotificationPreferencesResponse>('/notifications/settings');
  }

  /**
   * Update authorized IP addresses
   */
  async updateIpWhitelist(ips: string[]): Promise<{ message?: string }> {
    try {
      return apiClientMethods.put('/merchants/ip-whitelist', { ips });
    } catch (error) {
      console.error('Failed to update IP whitelist:', error);
      throw error;
    }
  }

  /**
   * Get API credentials
   */
  async getCredentials(): Promise<CredentialsResponse> {
    try {
      return apiClientMethods.get<CredentialsResponse>('/merchants/credentials');
    } catch (error) {
      console.error('Failed to get credentials:', error);
      throw error;
    }
  }

  /**
   * Regenerate API credentials
   */
  async regenerateCredentials(): Promise<RegenerateCredentialsResponse> {
    try {
      return apiClientMethods.post<RegenerateCredentialsResponse>(
        '/merchants/credentials/regenerate'
      );
    } catch (error) {
      console.error('Failed to regenerate credentials:', error);
      throw error;
    }
  }

  /**
   * Update webhook endpoint URL
   */
  async updateWebhookUrl(webhookUrl: string | null): Promise<UpdateWebhookUrlResponse> {
    try {
      return apiClientMethods.put<UpdateWebhookUrlResponse>('/merchants/webhook', {
        webhookUrl: webhookUrl || null,
      });
    } catch (error) {
      console.error('Failed to update webhook URL:', error);
      throw error;
    }
  }

  async setWebhookPaused(paused: boolean): Promise<{ webhookPaused: boolean }> {
    return apiClientMethods.put('/merchants/webhook/pause', { paused });
  }

  async getWebhookDeliveryIssues(): Promise<WebhookDeliveryIssue[]> {
    return apiClientMethods.get('/merchants/webhook-deliveries');
  }

  async retryWebhookDelivery(id: string): Promise<{ message: string }> {
    return apiClientMethods.post(`/merchants/webhook-deliveries/${id}/retry`);
  }

  /**
   * Regenerate webhook signing secret
   */
  async regenerateWebhookSecret(): Promise<RegenerateWebhookSecretResponse> {
    try {
      return apiClientMethods.post<RegenerateWebhookSecretResponse>(
        '/merchants/webhook/regenerate-secret'
      );
    } catch (error) {
      console.error('Failed to regenerate webhook secret:', error);
      throw error;
    }
  }

  /**
   * Dispatch a test webhook ping
   */
  async testWebhook(): Promise<TestWebhookResponse> {
    try {
      return apiClientMethods.post<TestWebhookResponse>('/merchants/webhook/test');
    } catch (error) {
      console.error('Failed to test webhook:', error);
      throw error;
    }
  }

  /**
   * Get detailed balance information
   */
  async getBalanceDetails(): Promise<BalanceDetailsResponse> {
    try {
      return apiClientMethods.get<BalanceDetailsResponse>('/merchants/balance');
    } catch (error) {
      console.error('Failed to get balance details:', error);
      throw error;
    }
  }

  /**
   * Get current merchant profile
   */
  async getProfile(): Promise<User> {
    try {
      return apiClientMethods.get<User>('/auth/me');
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  }

  /**
   * Reactivate a dormant account after confirming business details
   */
  async reactivateAccount(): Promise<ReactivateAccountResponse> {
    try {
      return apiClientMethods.post<ReactivateAccountResponse>('/merchants/reactivate');
    } catch (error) {
      console.error('Failed to reactivate account:', error);
      throw error;
    }
  }

  /**
   * Get detailed balance information (Aliased for Withdrawal page)
   */
  async getBalance(): Promise<BalanceDetailsResponse> {
    return this.getBalanceDetails();
  }
  /**
   * Get real-time notifications
   */
  async getNotifications(
    params: { page?: number; limit?: number; unreadOnly?: boolean } = {}
  ): Promise<NotificationListResponse> {
    try {
      const queryParams = new URLSearchParams({
        page: (params.page || 1).toString(),
        limit: (params.limit || 20).toString(),
        unreadOnly: (params.unreadOnly || false).toString(),
      });
      return apiClientMethods.get<NotificationListResponse>(
        `/notifications?${queryParams.toString()}`
      );
    } catch (error) {
      console.error('Failed to get notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(id: string): Promise<{ message?: string }> {
    try {
      return apiClientMethods.patch(`/notifications/${id}/read`, {});
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead(): Promise<{ message?: string }> {
    try {
      return apiClientMethods.patch('/notifications/read-all', {});
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      throw error;
    }
  }

  /**
   * Get disbursement fee tiers
   */
  async getDisbursementFeeTiers(): Promise<any[]> {
    try {
      return apiClientMethods.get<any[]>('/merchants/disbursement-fee-tiers');
    } catch (error) {
      console.error('Failed to get disbursement fee tiers:', error);
      throw error;
    }
  }

  /**
   * Request phone verification code via SMS
   */
  async sendPhoneVerificationOtp(
    phoneNumber?: string
  ): Promise<{ message: string; phoneNumber?: string; phoneVerified?: boolean }> {
    try {
      return apiClientMethods.post('/merchants/phone/send-otp', { phoneNumber });
    } catch (error) {
      console.error('Failed to send phone verification OTP:', error);
      throw error;
    }
  }

  /**
   * Verify phone number with OTP
   */
  async verifyPhoneOtp(
    otp: string,
    phoneNumber?: string
  ): Promise<{ message: string; phoneVerified: boolean; phoneNumber?: string; user?: User }> {
    try {
      return apiClientMethods.post('/merchants/phone/verify-otp', { otp, phoneNumber });
    } catch (error) {
      console.error('Failed to verify phone OTP:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const merchantService = new MerchantService();
